import React from "react";
import { Link } from "react-router-dom";
import useInView from "./useInView";

// Photographs, so WebP with a JPEG fallback -- not the PNGs these were
// delivered as. PNG is lossless and built for flat colour and sharp edges; as
// photographs these carried about 16x the bytes they needed (2788 KB of PNG
// against 175 KB of WebP for the same images at the same dimensions).
//
// Both formats are imported because <picture> PICKS one, it does not fetch
// both: a browser with no WebP support gets the JPEG and everyone else never
// requests it. The .png originals stay on disk as the source to re-encode
// from, and are deliberately not imported, so webpack never emits them.
//
// No resizing. The cards render at most 280 CSS px wide. The older sources are
// 533-534 px, which is about right for a 2x display; the two newest
// (waiting room and mall) arrived at ~360 px, so they are sharp at 1x and
// slightly soft on a retina screen. Worth knowing rather than worth fixing --
// upscaling adds bytes and no detail. If they are ever re-exported, ~550 px is
// the number to ask for.
import waitingRoomWebp from "../../images/examples/waiting-room-example.webp";
import waitingRoomJpg from "../../images/examples/waiting-room-example.jpg";
import cafeWebp from "../../images/examples/cafe-example.webp";
import cafeJpg from "../../images/examples/cafe-example.jpg";
import mallWebp from "../../images/examples/mall-example.webp";
import mallJpg from "../../images/examples/mall-example.jpg";
import hotelLobbyWebp from "../../images/examples/hotel-lobby-example.webp";
import hotelLobbyJpg from "../../images/examples/hotel-lobby-example.jpg";

// "Your audience is already there." A full-bleed band of venue types, to
// establish the shape of the network without claiming it already exists.
//
// The cards carry a venue type, where the screen sits inside it, and the
// screen format — categories, not customers. No town names, no counts, no
// logos: those would be claims, and the section works without them because
// what it needs to communicate is "this is a distributed physical network",
// not "here is our coverage".
//
// The venue types are chosen to match the photographs, not the other way
// round. A card whose picture is a stand-in while its neighbours are specific
// is the one the eye goes to, so a venue type without a photograph does not
// get a card, however much the pitch wants it there.
//
// --- which four, and why the salon is not among them ----------------------
//
// Storefront and Home window are gone. Both photographs are good and both are
// of the old model: a screen in a pane of glass, seen from the pavement. They
// are unreferenced rather than deleted (src/images/examples/), the same way
// vendor-panel-{1,2,3} are — a copy-paste away if a window-facing category
// ever comes back.
//
// The salon is gone from the rail too, and for the opposite reason: it is the
// hero photograph. With only three indoor pictures the rail had to share it,
// which is exactly the "ran out of material" look this comment used to warn
// about. Waiting room and Mall are new, so the two sections no longer repeat.
//
// THREE OF THE FOUR HAVE SEATING IN FRAME, and that is the selection rule
// rather than a coincidence. Chairs are the argument: they say somebody is
// sitting here for half an hour, which is the entire reason an indoor screen
// is worth buying and the one thing a window cannot claim. A photograph of a
// screen on an empty wall would be a picture of a television.
//
// The advertisements inside the photographs are invented ("Healthy Families
// Brighter Tomorrows", "Shop Local Support Local"). That is the same rule the
// rest of the page follows in the opposite direction: no real brand appears,
// because a real logo on a demo screen is a claim about a customer. The
// caption below the rail says these are mockups, and it has to stay for
// exactly that reason — including for the clinic, where invented services on
// an invented screen must not read as a real practice.

const PLACES = [
    {
        // First, because it is the clearest dwell story on the page: nobody
        // waits in one of these for four seconds.
        type: "Waiting room",
        format: 'Wall mounted · 50"',
        webp: waitingRoomWebp,
        jpg: waitingRoomJpg,
        alt: "A landscape display on the wall of a family waiting room, above "
            + "armchairs and a low table of children's toys.",
    },
    {
        type: "Café",
        format: 'Above the counter · 32"',
        webp: cafeWebp,
        jpg: cafeJpg,
        alt: "A landscape display mounted above a café counter, showing a drinks menu.",
    },
    {
        type: "Mall",
        format: 'Seating area · 55"',
        webp: mallWebp,
        jpg: mallJpg,
        alt: "A landscape display on a shopping centre wall above a sofa, with "
            + "shoppers passing storefronts beyond.",
    },
    {
        // Last, and the only portrait format in the rail — so the row ends on
        // the card that says these are not all the same shape of screen.
        type: "Hotel lobby",
        format: 'Floor totem · 55"',
        webp: hotelLobbyWebp,
        jpg: hotelLobbyJpg,
        alt: "A free-standing portrait totem in a hotel lobby beside seating and planting.",
    },
];

export default function RealWorld() {
    const [ref, inView] = useInView();

    return (
        <section className={"rw" + (inView ? " is-in" : "")} ref={ref}>
            <div className="rw__head">
                <h2 className="rw__title">
                    Your audience is<br />already there.
                </h2>
                <p className="rw__lede">
                    Reach people while they’re eating, waiting, shopping or
                    working out — on screens inside the local places they
                    already spend time in.
                </p>
            </div>

            {/* Full-bleed and horizontally scrollable on narrow viewports rather
                than reflowing to a stack: the row reading as a continuous strip is
                what makes it feel like a network instead of a list. */}
            <div className="rw__rail" role="list">
                {PLACES.map((place, i) => (
                    <article
                        className="rw__card"
                        key={place.type}
                        role="listitem"
                        style={{ "--i": i }}
                    >
                        <div className="rw__screen">
                            <picture>
                                <source srcSet={place.webp} type="image/webp" />
                                <img
                                    className="rw__photo"
                                    src={place.jpg}
                                    alt={place.alt}
                                    /* Below the fold in every viewport, so nothing
                                       here competes with the hero for first paint. */
                                    loading="lazy"
                                    decoding="async"
                                />
                            </picture>
                        </div>
                        <div className="rw__meta">
                            <h3>{place.type}</h3>
                            <p>{place.format}</p>
                        </div>
                    </article>
                ))}
            </div>

            {/* Directly under the cards it is describing. It was below the
                CTA, which put a caption after the thing that follows the
                thing it captions. */}
            <p className="rw__note">
                Illustrative mockups of the screen formats WallBurst supports.
            </p>

            {/* The section answers "what is a WallBurst screen"; this is the
                obvious next move once it has. */}
            <div className="rw__cta">
                <Link to="/devices" className="wb-btn wb-btn--secondary">
                    Explore locations →
                </Link>
            </div>
        </section>
    );
}
